import {
	ActionButton,
	Button,
	ConfirmModal,
	ExportDropdown,
	useConfirmModal,
	useToast,
} from '@practium/ui';
import { Copy, FileQuestion } from 'lucide-react';
import { useExportParcelling } from '../../api/hooks/parcelling/useExportParcelling';
import {
	ClientSelectionModal,
	ExpandableFloraCard,
	FarmSelectionModal,
	FloraFillAnimation,
	NutrientTable,
	DynamicNutrientTable,
	ParcellingFormSection,
	ParcellingListCard,
	ParcellingTabs,
} from '../components';
import { useParcellingPage } from '../hooks/useParcellingPage';
import type { BindingUsersResponse, ParcellingPageConfig, PropDTOResponse } from '../types';
import styles from './ParcellingPage.module.css';
import { ParcellingTypeSelector } from '../components/ParcellingTypeSelector/ParcellingTypeSelector';

export interface ParcellingPageProps {
	config: ParcellingPageConfig;
	page?: number;
	onPageChange?: (page: number) => void;
	fetchBindingUsers?: (query: string) => Promise<BindingUsersResponse>;
	fetchFarms?: (params?: { userId?: string; query?: string }) => Promise<PropDTOResponse>;
}

export const ParcellingPage = ({
	config,
	page,
	onPageChange,
	fetchBindingUsers,
	fetchFarms,
}: ParcellingPageProps) => {
	const toast = useToast();
	const exportParcelling = useExportParcelling();
	const {
		confirm,
		isOpen,
		options,
		handleConfirm,
		handleCancel: handleModalCancel,
	} = useConfirmModal();
	const {
		activeTab,
		handleTabChange,
		isEditing,
		permissions,
		parcellings,
		selectedParcellingId,
		selectedParcelling,
		formData,
		errors,
		isDirty,
		isSubmitting,
		updateParcels,
		updateDynamicParcel,
		updateFloraDynamicParcel,
		handleSubmit,
		handleFloraClick,
		handleEdit,
		handleCancel,
		handleDelete,
		handleDuplicate,
		handleAddNew,
		handleSelectParcelling,
		handleFieldChange,
		hasData,
		selectedCanEdit,
		selectedCanDelete,
		canBind,
		canBindFarm,
		bindingUsers,
		bindingUserDisplayName,
		isAdmin,
		canManagePublicVisibility,
		clientModalOpen,
		farmModalOpen,
		handleOpenClientModal,
		handleCloseClientModal,
		handleClientSelect,
		handleOpenFarmModal,
		handleCloseFarmModal,
		handleFarmSelect,
		farms,
		selectedFarmIds,
		selectedFarmName,
		isFloraExpanded,
		isFloraAnimating,
		floraParcels,
		floraDynamicParcel,
		updateFloraParcels,
		handleToggleFloraExpand,
		handleFloraSendMessage,
	} = useParcellingPage({ config, toast, confirm, fetchBindingUsers, fetchFarms });

	const handleExport = async (format: 'pdf' | 'xlsx') => {
		try {
			const { file, filename } = await exportParcelling.mutateAsync({
				format
			});
			const url = URL.createObjectURL(file);
			const link = document.createElement('a');
			link.href = url;
			link.download = filename;
			document.body.appendChild(link);
			try {
				link.click();
			} finally {
				link.remove();
				window.setTimeout(() => URL.revokeObjectURL(url), 1000);
			}
		} catch {
			toast.error('Erro ao exportar parcelamentos. Tente novamente.');
		}
	};

	return (
		<>
			<div className={styles.page}>
				<div className={styles.mainContent}>
					<div className={`${styles.card} ${isSubmitting ? styles.loading : ''}`}>
						{isSubmitting && (
							<div className={styles.loadingOverlay}>
								<div className={styles.loadingSpinner} />
							</div>
						)}
						<div className={styles.header}>
							<div className={styles.headerText}>
								<h1 className={styles.title}>Informações Gerais</h1>
								<p className={styles.subtitle}>
									Informações gerais sobre o parcelamento em questão
								</p>
							</div>
							<div className={styles.headerActions}>
								{permissions.canCreate && !isEditing && (
									<ActionButton text="Adicionar" onClick={handleAddNew} />
								)}
								{permissions.canEdit && !isEditing && selectedCanEdit && (
									<ActionButton text="Editar" onClick={handleEdit} />
								)}
								{permissions.canExport && hasData && (
									<ExportDropdown
										disabled={exportParcelling.isPending}
										label={exportParcelling.isPending ? 'Exportando…' : 'Exportar'}
										onExportExcel={() => void handleExport('xlsx')}
										onExportPdf={() => void handleExport('pdf')}
									/>
								)}
									{permissions.canDuplicate && !isEditing && hasData && selectedParcelling && selectedParcelling.typeParcelling !== 'dynamic' && (
									<button
										type="button"
										className={styles.iconButton}
										onClick={handleDuplicate}
										title="Duplicar parcelamento"
										aria-label="Duplicar parcelamento"
									>
										<Copy className={styles.iconButtonIcon} />
									</button>
								)}
							</div>
						</div>

						{isAdmin && (
							<ParcellingTypeSelector
								value={formData.typeParcelling}
								onChange={handleFieldChange.typeParcelling}
								canSelectDynamic={isAdmin}
								disabled={isSubmitting || (!!selectedParcelling && !isEditing) || (!!selectedParcelling && !selectedParcelling._id.startsWith('temp-'))}
							/>
						)}

						{selectedParcelling ? (
							<>
								<ParcellingFormSection
									name={formData.name}
									culture={formData.culture}
									cropStage={formData.cropStage}
									isPublic={formData.isPublic}
									note={formData.note}
									bindingUserDisplayName={bindingUserDisplayName}
									selectedFarmName={selectedFarmName}
									canBindUser={canBind && formData.typeParcelling === 'fixed'}
									canBindFarm={canBindFarm && formData.typeParcelling === 'fixed'}
									showMaturationCycle={formData.typeParcelling === 'dynamic'}
									maturationCycle={formData.maturationCycle}
									onMaturationCycleChange={handleFieldChange.maturationCycle}
									canManagePublicVisibility={canManagePublicVisibility && formData.typeParcelling === 'fixed'}
									onNameChange={handleFieldChange.name}
									onCultureChange={handleFieldChange.culture}
									onIsPublicChange={handleFieldChange.isPublic}
									onNoteChange={handleFieldChange.note}
									onOpenClientModal={handleOpenClientModal}
									onOpenFarmModal={handleOpenFarmModal}
									isEditing={isEditing}
									errors={errors}
								/>

								<div className={styles.section}>
									<div className={styles.sectionHeader}>
										<h2 className={styles.title}>Dados do Parcelamento</h2>
										<h3 className={styles.subtitle}>
											Parâmetros do parcelamento relacionados aos elementos do solo
										</h3>
									</div>
									<ParcellingTabs activeTab={activeTab} onTabChange={handleTabChange} isEditing={isEditing} disabled={isSubmitting} />

									{formData.typeParcelling === 'dynamic' ? (
										<div className={styles.floraTabContent}>
											<DynamicNutrientTable
												data={activeTab === 'flora' ? floraDynamicParcel : formData.dynamicParcel}
												onChange={activeTab === 'flora' ? updateFloraDynamicParcel : updateDynamicParcel}
												isEditing={isEditing && !isFloraAnimating && !isSubmitting}
												errors={errors}
											/>
											<FloraFillAnimation isAnimating={isFloraAnimating} />
										</div>
									) : activeTab === 'manual' ? (
										<NutrientTable
											data={formData.parcels}
											onChange={updateParcels}
											isEditing={isEditing}
										/>
									) : (
										<div className={styles.floraTabContent}>
											<NutrientTable
												data={floraParcels}
												onChange={updateFloraParcels}
												isEditing={isEditing}
											/>
											<FloraFillAnimation isAnimating={isFloraAnimating} />
										</div>
									)}
								</div>

								{isEditing && (
									<div className={styles.actions}>
										{isDirty && (
											<div className={styles.unsavedIndicator}>
												<span className={styles.unsavedDot} />
												<span>Alterações não salvas</span>
											</div>
										)}
										<div className={styles.actionButtons}>
											{permissions.canDelete && selectedCanDelete && !selectedParcelling?._id.startsWith('temp-') && (
												<Button
													variant="danger"
													className={styles.button}
													onClick={handleDelete}
													disabled={isSubmitting}
												>
													Excluir
												</Button>
											)}
											<Button
												variant="secondary"
												className={styles.button}
												onClick={handleCancel}
												disabled={isSubmitting}
											>
												Cancelar
											</Button>
											<Button
												variant="primary"
												className={styles.button}
												onClick={() => handleSubmit()}
												disabled={isSubmitting || isFloraAnimating || !isDirty}
											>
												{isSubmitting ? 'Salvando...' : 'Salvar Parcelamento'}
											</Button>
										</div>
									</div>
								)}
							</>
						) : (
							<div className={styles.emptyState}>
								<div className={styles.emptyContent}>
									<FileQuestion className={styles.emptyIcon} />
									<p>Selecione um parcelamento ou adicione um novo</p>
								</div>
							</div>
						)}
					</div>
				</div>

				<aside className={styles.sidebar}>
					<div
						className={`${styles.listWrapper} ${isFloraExpanded ? styles.listWrapperShrunk : ''}`}
					>
						<ParcellingListCard
							parcellings={parcellings}
							selectedId={selectedParcellingId}
							onSelect={handleSelectParcelling}
							page={page ?? 1}
							totalPages={Math.max(1, config.mockData.totalPages || Math.ceil((config.mockData.totalDocs || parcellings.length) / 15))}
							onPageChange={onPageChange}
						/>
					</div>
					{isEditing && !isSubmitting && (
						<div
							className={`${styles.floraWrapper} ${isFloraExpanded ? styles.floraWrapperExpanded : ''}`}
						>
							<ExpandableFloraCard
								onFloraClick={handleFloraClick}
								onSendMessage={handleFloraSendMessage}
								isExpanded={isFloraExpanded}
								onToggleExpand={handleToggleFloraExpand}
							/>
						</div>
					)}
				</aside>
			</div>

			<ClientSelectionModal
				isOpen={clientModalOpen && formData.typeParcelling === 'fixed'}
				onClose={handleCloseClientModal}
				onSave={handleClientSelect}
				users={bindingUsers}
				farms={farms}
				selectedUserIds={formData.bindingUserIds}
				showAllOption={isAdmin}
			/>

			<FarmSelectionModal
				isOpen={farmModalOpen && formData.typeParcelling === 'fixed'}
				onClose={handleCloseFarmModal}
				onSave={handleFarmSelect}
				farms={farms}
				selectedFarmIds={selectedFarmIds}
				showAllOption={isAdmin}
			/>

			<ConfirmModal
				isOpen={isOpen}
				title={options.title}
				message={options.message}
				confirmText={options.confirmText}
				cancelText={options.cancelText}
				variant={options.variant}
				onConfirm={handleConfirm}
				onCancel={handleModalCancel}
			/>
		</>
	);
};
